<script lang="ts" setup>
import { ref, computed, inject, Ref, onMounted, nextTick, onBeforeUnmount } from 'vue';
import { useScroll, useElementSize, UseScrollReturn } from '@vueuse/core';
import { useMedia } from '@/plugins/media';
import { DocumentPreviewType } from '.';
import { Resource as FileResource } from "@/modules/core/files/services/StorageService";
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { DetailsBuilderContract } from '@/components/builder/details';
import FileReader from "@/views/partials/components/attachments/FIleReader.vue";
import properties from '../../properties';
import { useMixins } from '@/plugins/mixins';

defineOptions({
    name: 'DocumentPreviewBlueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "details": null,
  "parent": null
});

const { desktop, tablet } = useMedia();
const { $filters } = useMixins();

const wrapperRef = ref<HTMLElement | null>(null);
const documentPreviewRef = ref<HTMLElement | null>(null);
const documentPreviewContentRef = ref<HTMLElement | null>(null);

const { height: heightContainer } = useElementSize(documentPreviewRef);
const { height: heightContent, } = useElementSize(documentPreviewContentRef);
const scrollData = ref<UseScrollReturn>(null);

const fileReaderOffset = ref('');

const filePreview = inject<Ref<FileResource>>('file-preview', null);
const fileUrl = computed(() => filePreview.value?.publicId ? `api/documents/file/${filePreview.value.publicId}/${filePreview.value.name}` : filePreview.value?.url);

const isScrollAvailable = computed(() => desktop() || tablet());
const offset = computed(() =>
{
    if (isScrollAvailable.value && scrollData.value)
    {
        const height = heightContainer.value - heightContent.value;
        const scrollY = scrollData.value.y;

        return scrollY > height ? height : scrollY;
    }

    return 0;
});
const blueprint = computed(() => props.blueprint);
const design = computed(() => props.details.designMode());

const maxHeight = ref('100%');
let resizeObserver: ResizeObserver | null = null;

function calculateMaxHeight()
{
    if (documentPreviewRef.value)
    {
        const navbarHeight = document.querySelector('.navbar')?.clientHeight ?? 0;
        const breadcrumbsHeight = document.querySelector('#breadcrumbs')?.clientHeight ?? 0;
        const toolbar = document.querySelector('.toolbar')?.clientHeight ?? 0;

        maxHeight.value = `calc(100vh - ${navbarHeight + breadcrumbsHeight + toolbar + 20}px)`;
    }
}

function watchScroll()
{
    const toolbarHeight = wrapperRef.value.querySelector('.toolbar')?.clientHeight ?? 0;
    const contentTop = documentPreviewContentRef.value?.getBoundingClientRect().top;

    const y = wrapperRef.value.getBoundingClientRect().top + toolbarHeight + 10;

    fileReaderOffset.value = Math.max(0, y - contentTop).toFixed(2) + 'px';
}

onMounted(() =>
{
    if (!design.value)
    {
        wrapperRef.value = document.querySelector('[data-name="wrapper"]');
        scrollData.value = useScroll(wrapperRef.value, { behavior: 'smooth' }) as any;

        nextTick(() =>
        {
            calculateMaxHeight();

            setTimeout(calculateMaxHeight, 100);

            if (document.readyState === 'complete')
            {
                calculateMaxHeight();
            }
            else
            {
                window.addEventListener('load', calculateMaxHeight, { once: true });
            }
        });

        if ('ResizeObserver' in window)
        {
            resizeObserver = new ResizeObserver(() =>
            {
                calculateMaxHeight();
            });

            if (documentPreviewRef.value)
            {
                resizeObserver.observe(documentPreviewRef.value);

                const parentElement = documentPreviewRef.value.parentElement;

                if (parentElement)
                {
                    resizeObserver.observe(parentElement);
                }
            }
        }

        window.addEventListener('resize', calculateMaxHeight);

        wrapperRef.value?.addEventListener('scroll', watchScroll);
        watchScroll();
    }


});

onBeforeUnmount(() =>
{
    window.removeEventListener('resize', calculateMaxHeight);
    window.removeEventListener('load', calculateMaxHeight);

    if (resizeObserver)
    {
        resizeObserver.disconnect();
        resizeObserver = null;
    }

    wrapperRef.value?.removeEventListener('scroll', watchScroll);
});
</script>

<template>
    <dynamic-details-component-wrapper :details="details" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div v-if="!design" ref="documentPreviewRef" class="h-100 overflow-hidden">
                <div ref="documentPreviewContentRef" class="position-relative h-100" :style="{ 'top': `${offset}px` }">
                    <file-reader v-if="filePreview && fileUrl" :src="fileUrl" class="w-100 h-100" :style="{ 'max-height': maxHeight, 'top': fileReaderOffset }" />
                </div>
            </div>
            <div v-else class="d-flex justify-content-center align-items-center h-100">
                <i class="far fa-ballot-check fa-8x"></i>
            </div>
        </template>
        <template #properties>
            <field-name v-model="blueprint.name" />
            <dynamic-details-accordion>
                <dynamic-details-accordion-item icon="far fa-table" :header="$t('[[[Prezentacja]]]')" :open="true">
                    <field-visible :details="details" :blueprint="blueprint" />
                </dynamic-details-accordion-item>
            </dynamic-details-accordion>
        </template>
    </dynamic-details-component-wrapper>
</template>

<style lang="scss" scoped>
.fa-ballot-check {
    color: #edeff1;
}

:deep(.file-reader) {
    max-height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
}

:deep(.file-reader iframe),
:deep(.file-reader object),
:deep(.file-reader embed) {
    width: 100%;
    height: 100%;
    min-height: 50vh;
    border: none;
    object-fit: contain;
}
</style>
